import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  Phone, 
  Mail, 
  User,
  Building2,
  Users,
  Info,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  XCircle,
  ChevronDown
} from 'lucide-react';
import { LoadMoreIndicator } from '@/components/common/LoadingStates';

const API = process.env.REACT_APP_BACKEND_URL;

// Pagination config
const COMMS_PAGE_SIZE = 20;
const COMMS_INITIAL_DISPLAY = 30;

// Communication type configuration
const COMM_TYPES = {
  internal_note: { label: "Internal Note", icon: MessageSquare, color: "bg-slate-500" },
  client_message: { label: "Client Message", icon: Building2, color: "bg-blue-500" },
  contractor_message: { label: "Contractor Message", icon: Users, color: "bg-amber-500" },
  resident_message: { label: "Resident Message", icon: User, color: "bg-green-500" },
  system_update: { label: "System Update", icon: Info, color: "bg-purple-500" },
  whatsapp_log: { label: "WhatsApp Log", icon: Phone, color: "bg-emerald-500" },
  email_log: { label: "Email Log", icon: Mail, color: "bg-cyan-500" },
  call_note: { label: "Call Note", icon: Phone, color: "bg-orange-500" }
};

// Delivery status badges
const DELIVERY_STATUS = {
  draft: { label: "Draft", color: "bg-slate-400", icon: Clock },
  sent: { label: "Sent", color: "bg-blue-500", icon: CheckCircle },
  delivered: { label: "Delivered", color: "bg-green-500", icon: CheckCircle },
  seen: { label: "Seen", color: "bg-teal-500", icon: Eye },
  failed: { label: "Failed", color: "bg-red-500", icon: XCircle }
};

// Filter tabs
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "internal_note", label: "Internal" },
  { key: "client_message", label: "Client" },
  { key: "contractor_message", label: "Contractor" },
  { key: "resident_message", label: "Resident" },
  { key: "system_update", label: "System" }
];

export default function CommunicationsHub({ 
  jobId, 
  communications = [], 
  onCommunicationAdded,
  readOnly = false 
}) {
  const [message, setMessage] = useState('');
  const [commType, setCommType] = useState('internal_note');
  const [visibility, setVisibility] = useState('internal');
  const [sending, setSending] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [localComms, setLocalComms] = useState(communications);
  // C4 FIX: Track locally added comms to prevent loss on parent refresh
  const [localAdditions, setLocalAdditions] = useState([]);
  
  // Pagination state for large communication lists
  const [displayCount, setDisplayCount] = useState(COMMS_INITIAL_DISPLAY);
  const [loadingMore, setLoadingMore] = useState(false);
  const listEndRef = useRef(null);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // C4 FIX: Reconcile incoming communications with local additions
  useEffect(() => {
    // Merge incoming comms with any local additions not yet in the incoming data
    const incomingIds = new Set(communications.map(c => c.id));
    const missingLocal = localAdditions.filter(c => !incomingIds.has(c.id));
    
    // Combine and sort by created_at descending
    const merged = [...missingLocal, ...communications];
    merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setLocalComms(merged);
    
    // Clean up local additions that are now in incoming data
    setLocalAdditions(prev => prev.filter(c => !incomingIds.has(c.id)));
  }, [communications]);
  
  // Filter communications
  const filteredComms = useMemo(() => {
    const filtered = activeFilter === 'all' 
      ? localComms 
      : localComms.filter(c => c.type === activeFilter);
    return filtered;
  }, [localComms, activeFilter]);
  
  // Paginated/visible communications
  const visibleComms = useMemo(() => {
    return filteredComms.slice(0, displayCount);
  }, [filteredComms, displayCount]);
  
  const hasMoreComms = filteredComms.length > displayCount;
  
  // Load more handler
  const handleLoadMore = useCallback(() => {
    setLoadingMore(true);
    // Simulate small delay for smooth UX
    setTimeout(() => {
      setDisplayCount(prev => prev + COMMS_PAGE_SIZE);
      setLoadingMore(false);
    }, 200);
  }, []);
  
  // Reset display count when filter changes
  useEffect(() => {
    setDisplayCount(COMMS_INITIAL_DISPLAY);
  }, [activeFilter]);
  
  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    // C2 FIX: Prevent duplicate sends while in progress
    if (sending) {
      return;
    }
    
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/phs/jobs/${jobId}/communications`,
        {
          type: commType,
          message: message.trim(),
          visibility,
          recipient_type: getRecipientType(commType),
          attachments: []
        },
        { headers }
      );
      
      if (res.data.success) {
        toast.success('Communication added');
        setMessage('');
        const newComm = res.data.communication;
        // C4 FIX: Track local addition for reconciliation
        setLocalAdditions(prev => [newComm, ...prev]);
        setLocalComms(prev => [newComm, ...prev]);
        onCommunicationAdded?.(newComm);
      }
    } catch (err) {
      console.error('Failed to add communication:', err);
      // Handle rate limiting error
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || 'Rate limit exceeded. Please wait.');
      } else {
        toast.error('Failed to add communication');
      }
    } finally {
      setSending(false);
    }
  };
  
  const getRecipientType = (type) => {
    switch (type) {
      case 'client_message': return 'client';
      case 'contractor_message': return 'contractor';
      case 'resident_message': return 'resident';
      default: return 'none';
    }
  };
  
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col h-full" data-testid="communications-hub">
      {/* Header */}
      <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-100 bg-slate-50 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-teal-600" />
            <span className="font-semibold text-slate-800 text-xs sm:text-sm">Communications</span>
            <Badge variant="secondary" className="text-[10px] sm:text-xs h-5">{localComms.length}</Badge>
          </div>
        </div>
      </div>
      
      {/* Input Section - MOBILE OPTIMIZED */}
      {!readOnly && (
        <div className="p-3 sm:p-4 border-b border-slate-100 bg-white shrink-0">
          <div className="space-y-2 sm:space-y-3">
            {/* Type and Visibility Selectors - Stack on mobile */}
            <div className="flex flex-wrap sm:flex-nowrap gap-2">
              <Select value={commType} onValueChange={setCommType}>
                <SelectTrigger className="flex-1 sm:w-[160px] h-10 sm:h-9 text-xs touch-manipulation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COMM_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-xs py-2.5">
                      <div className="flex items-center gap-2">
                        <config.icon className="h-3.5 w-3.5" />
                        <span>{config.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger className="flex-1 sm:w-[120px] h-10 sm:h-9 text-xs touch-manipulation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal" className="text-xs py-2.5">Internal Only</SelectItem>
                  <SelectItem value="client" className="text-xs py-2.5">Client Visible</SelectItem>
                  <SelectItem value="contractor" className="text-xs py-2.5">Contractor Visible</SelectItem>
                  <SelectItem value="public" className="text-xs py-2.5">All Visible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Message Input - Larger touch targets on mobile */}
            <div className="flex gap-2">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="min-h-[56px] sm:min-h-[60px] text-sm resize-none flex-1"
                data-testid="comm-message-input"
              />
            </div>
            
            {/* Actions - MOBILE OPTIMIZED touch targets */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" className="text-xs text-slate-500 h-10 sm:h-8 px-3 touch-manipulation">
                <Paperclip className="h-4 w-4 sm:h-3 sm:w-3 mr-1.5 sm:mr-1" />
                Attach
              </Button>
              <Button 
                size="sm" 
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-xs h-10 sm:h-8 px-4 sm:px-3 touch-manipulation"
                data-testid="comm-send-btn"
              >
                <Send className="h-4 w-4 sm:h-3 sm:w-3 mr-1.5 sm:mr-1" />
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Filter Tabs - MOBILE OPTIMIZED with larger touch targets */}
      <div className="px-3 sm:px-4 py-2 border-b border-slate-100 bg-slate-50/50 overflow-x-auto shrink-0">
        <div className="flex gap-1.5">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`px-3 py-1.5 sm:px-2.5 sm:py-1 text-xs rounded-lg whitespace-nowrap transition touch-manipulation ${
                activeFilter === tab.key
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-600 active:bg-slate-100 border border-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Communications List - MOBILE OPTIMIZED with pagination */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {filteredComms.length === 0 ? (
          <div className="p-6 sm:p-8 text-center text-slate-400 text-sm">
            No communications yet
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {visibleComms.map((comm, idx) => {
              const typeConfig = COMM_TYPES[comm.type] || COMM_TYPES.internal_note;
              const TypeIcon = typeConfig.icon;
              const deliveryConfig = DELIVERY_STATUS[comm.delivery_status];
              
              return (
                <div 
                  key={comm.id || idx} 
                  className="p-3 sm:hover:bg-slate-50/50 transition active:bg-slate-50 touch-manipulation"
                  data-testid={`comm-item-${idx}`}
                >
                  <div className="flex gap-2.5 sm:gap-3">
                    {/* Type Icon */}
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon className="h-4 w-4 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-slate-700">
                          {typeConfig.label}
                        </span>
                        <span className="text-[10px] text-slate-400">•</span>
                        <span className="text-[10px] text-slate-500">
                          {comm.author || 'System'} ({comm.author_role || 'system'})
                        </span>
                        <span className="text-[10px] text-slate-400 ml-auto">
                          {formatTime(comm.created_at)}
                        </span>
                      </div>
                      
                      {/* Message */}
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">
                        {comm.message}
                      </p>
                      
                      {/* Footer */}
                      <div className="flex items-center gap-2 mt-1.5">
                        {/* Visibility Badge */}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          comm.visibility === 'internal' 
                            ? 'bg-slate-100 text-slate-500'
                            : 'bg-blue-50 text-blue-600'
                        }`}>
                          {comm.visibility}
                        </span>
                        
                        {/* Delivery Status */}
                        {deliveryConfig && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 ${
                            deliveryConfig.color === 'bg-green-500' ? 'bg-green-50 text-green-600' :
                            deliveryConfig.color === 'bg-red-500' ? 'bg-red-50 text-red-600' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            <deliveryConfig.icon className="h-2.5 w-2.5" />
                            {deliveryConfig.label}
                          </span>
                        )}
                        
                        {/* Attachments */}
                        {comm.attachments?.length > 0 && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Paperclip className="h-2.5 w-2.5" />
                            {comm.attachments.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Load More Indicator */}
            <LoadMoreIndicator
              loading={loadingMore}
              hasMore={hasMoreComms}
              onLoadMore={handleLoadMore}
            />
            <div ref={listEndRef} />
          </div>
        )}
      </div>
    </div>
  );
}

export { COMM_TYPES, DELIVERY_STATUS };
